/**
 * @module useRewardsModalDisplay
 * @description Custom hook for managing rewards modal display state with localStorage persistence
 */

import { useState } from 'react';

/**
 * @function useRewardsModalDisplay
 * @description Hook that provides state management for rewards modal display with localStorage persistence
 *
 * @param {string} [key='rewards-modal-display'] - The localStorage key to store the display state
 * @param {boolean} [initialValue=true] - Default value if nothing is found in localStorage
 * @returns {Array} - A stateful value and a function to update it:
 *   [0] {boolean} storedValue - The current display state (from localStorage or initialValue)
 *   [1] {Function} setValue - Function to update the display state
 *
 * @example
 * const [showRewardsModal, setShowRewardsModal] = useRewardsModalDisplay();
 *
 * // Later in your component:
 * return (
 *   <>
 *     {showRewardsModal && <RewardsModal onClose={() => setShowRewardsModal(false)} />}
 *     <button onClick={() => setShowRewardsModal(true)}>Show Rewards</button>
 *   </>
 * );
 */
export default function useRewardsModalDisplay(key = 'rewards-modal-display', initialValue = true) {
  /**
   * Initialize state with value from localStorage or use initialValue
   */
  const [storedValue, setStoredValue] = useState(() => {
    const item = localStorage.getItem(key);
    return item !== null ? JSON.parse(item) : initialValue;
  });

  /**
   * @function setValue
   * @description Updates both the React state and localStorage
   * @param {boolean} value - New display state value
   */
  const setValue = (value) => {
    setStoredValue(value);
    localStorage.setItem(key, JSON.stringify(value));
  };

  return [storedValue, setValue];
}
